import { Check, Clock3, ScanSearch, UserRound, X } from 'lucide-react';

import { useTranslate } from '../i18n/useI18n';

import type { AnalysisConfig } from '../domain/analysis';
import type { MessageKey } from '../i18n/messages';
import type { Translate } from '../i18n/types';
import type { AnalysisResult, FaceResult } from '../lib/faceAnalysis';
import type { ReactElement } from 'react';

const expressionLabelKeys: Readonly<Record<string, MessageKey>> = {
    neutral: 'results.expression.neutral',
    happy: 'results.expression.happy',
    sad: 'results.expression.sad',
    angry: 'results.expression.angry',
    fearful: 'results.expression.fearful',
    disgusted: 'results.expression.disgusted',
    surprised: 'results.expression.surprised',
};

function getReferenceLabel(
    labels: string[],
    index: number | undefined,
    translate: Translate
): string {
    if (index === undefined) return translate('results.reference');
    const label = labels[index];
    return label !== undefined && label.length > 0
        ? label
        : translate('results.referenceNumber', { number: index + 1 });
}

function FaceDetails({
    face,
    config,
    translate,
}: {
    face: FaceResult;
    config: AnalysisConfig;
    translate: Translate;
}): ReactElement {
    const topExpression =
        face.expressions !== undefined
            ? Object.entries(face.expressions).sort(([, a], [, b]) => b - a)[0]
            : undefined;
    const topExpressionLabelKey =
        topExpression === undefined ? undefined : expressionLabelKeys[topExpression[0]];
    return (
        <div className='face-metrics'>
            <span>
                {translate('results.metric.detection')}{' '}
                <strong>%{Math.round(face.score * 100)}</strong>
            </span>
            {topExpression !== undefined && (
                <span>
                    {translate('results.metric.expression')}{' '}
                    <strong>
                        {topExpressionLabelKey === undefined
                            ? topExpression[0]
                            : translate(topExpressionLabelKey)}{' '}
                        %{Math.round(topExpression[1] * 100)}
                    </strong>
                </span>
            )}
            {face.age !== undefined && (
                <span>
                    {translate('results.metric.age')} <strong>≈{Math.round(face.age)}</strong>
                </span>
            )}
            {face.gender !== undefined && face.gender.length > 0 && (
                <span>
                    {translate('results.metric.gender')}{' '}
                    <strong>
                        {translate(
                            face.gender === 'female'
                                ? 'results.gender.female'
                                : 'results.gender.male'
                        )}{' '}
                        %{Math.round((face.genderProbability ?? 0) * 100)}
                    </strong>
                </span>
            )}
            {face.match !== undefined && (
                <span>
                    {translate('results.metric.distance')}{' '}
                    <strong>
                        {face.match.distance.toFixed(3)} / {config.distanceThreshold.toFixed(2)}
                    </strong>
                </span>
            )}
        </div>
    );
}

export function ResultsPanel({
    result,
    config,
    labels,
    onLabelChange,
}: {
    result: AnalysisResult;
    config: AnalysisConfig;
    labels: string[];
    onLabelChange: (index: number, label: string) => void;
}): ReactElement {
    const translate = useTranslate();
    const matchedCount = result.haystack.filter((face) => face.match?.isMatch === true).length;
    return (
        <section
            className='results-panel'
            aria-labelledby='results-title'
        >
            <div className='results-heading'>
                <div>
                    <span className='eyebrow'>{translate('results.eyebrow')}</span>
                    <h2 id='results-title'>{translate('results.title')}</h2>
                </div>
                <div className='result-stats'>
                    {config.features.matching && (
                        <span>
                            <Check aria-hidden='true' />{' '}
                            {translate('results.matches', { count: matchedCount })}
                        </span>
                    )}
                    <span>
                        <ScanSearch aria-hidden='true' />{' '}
                        {translate('results.targets', { count: result.haystack.length })}
                    </span>
                    <span>
                        <Clock3 aria-hidden='true' />{' '}
                        {translate('results.seconds', {
                            seconds: (result.durationMs / 1000).toFixed(2),
                        })}
                    </span>
                </div>
            </div>
            {config.features.matching && (
                <div className='result-section'>
                    <h3>
                        {translate('results.references')} <span>{result.reference.length}</span>
                    </h3>
                    <div className='face-list'>
                        {result.reference.map((face, index) => (
                            <article
                                className='face-row'
                                key={face.id}
                            >
                                <span className='face-index'>
                                    <UserRound aria-hidden='true' /> {index + 1}
                                </span>
                                <label className='reference-name'>
                                    <span className='visually-hidden'>
                                        {translate('results.referenceLabel', { number: index + 1 })}
                                    </span>
                                    <input
                                        value={labels[index] ?? ''}
                                        placeholder={translate('results.referenceNumber', {
                                            number: index + 1,
                                        })}
                                        onChange={(event) => {
                                            onLabelChange(index, event.target.value);
                                        }}
                                    />
                                </label>
                                <FaceDetails
                                    face={face}
                                    config={config}
                                    translate={translate}
                                />
                            </article>
                        ))}
                    </div>
                </div>
            )}
            <div className='result-section'>
                <h3>
                    {translate('results.targetFaces')} <span>{result.haystack.length}</span>
                </h3>
                {result.haystack.length > 0 ? (
                    <div className='face-list'>
                        {result.haystack.map((face, index) => {
                            return (
                                <article
                                    className='face-row'
                                    key={face.id}
                                >
                                    <span className='face-index'>
                                        <UserRound aria-hidden='true' /> {index + 1}
                                    </span>
                                    <span
                                        className={`match-badge${face.match?.isMatch === true ? ' is-match' : ''}`}
                                    >
                                        {face.match !== undefined ? (
                                            face.match.isMatch ? (
                                                <Check aria-hidden='true' />
                                            ) : (
                                                <X aria-hidden='true' />
                                            )
                                        ) : (
                                            <ScanSearch aria-hidden='true' />
                                        )}
                                        {face.match !== undefined
                                            ? face.match.isMatch
                                                ? getReferenceLabel(
                                                      labels,
                                                      face.match.referenceIndex,
                                                      translate
                                                  )
                                                : translate('results.noMatch')
                                            : translate('results.faceNumber', {
                                                  number: index + 1,
                                              })}
                                    </span>
                                    <FaceDetails
                                        face={face}
                                        config={config}
                                        translate={translate}
                                    />
                                </article>
                            );
                        })}
                    </div>
                ) : (
                    <p className='empty-result'>{translate('results.noFaces')}</p>
                )}
            </div>
        </section>
    );
}
